import { useEffect, useState } from 'react';

import { LINE_SEGMENTS, monitorApi } from '../../api/monitor';
import type { LineDetail, LineNumber } from '../../api/monitor';
import { operatorPill } from '../../lib/operators';
import { formatLine, formatUsage, lineOwner, usageHint } from '../../lib/lines';
import { formatCnpj } from '../../lib/masters';

interface LineDetailSheetProps {
  /** A linha clicada na listagem — já dá o cabeçalho enquanto o detalhe carrega. */
  line: LineNumber;
  onClose: () => void;
}

/** `1,4` — GB no formato brasileiro; vazio vira travessão. */
function gb(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

/** Uma linha de campo do detalhe (rótulo à esquerda, valor à direita). */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <span className="text-ink-400 shrink-0 text-[11.5px] font-bold">{label}</span>
      <span className="text-ink-900 min-w-0 text-right text-[12.5px] font-semibold break-words">
        {value || '—'}
      </span>
    </div>
  );
}

/** Barra de consumo 0–100% com o par "consumo / franquia" ao lado. */
function UsageBar({
  label,
  consumed,
  hired,
}: {
  label: string;
  consumed: number | null;
  hired: number | null;
}) {
  const share =
    hired !== null && hired > 0 && consumed !== null
      ? Math.min(100, Math.max(0, (consumed / hired) * 100))
      : 0;

  const text =
    hired !== null
      ? `${gb(consumed)} / ${gb(hired)} GB`
      : consumed !== null
        ? `${gb(consumed)} GB · franquia não informada`
        : 'sem consumo';

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-bold">{label}</span>
        <span className="text-ink-600 text-[11.5px] font-semibold">{text}</span>
      </div>
      <div className="bg-surface-200 h-2 overflow-hidden rounded-full">
        <div className="bg-navy-800 h-full rounded-full" style={{ width: `${share}%` }} />
      </div>
    </div>
  );
}

/**
 * Side sheet da linha: abre pela direita ao clicar numa linha da listagem e
 * mostra o que a linha é (CNPJ, master, cliente, conta) e o consumo — na Claro,
 * a quebra por segmento (Web, WhatsApp, Social, Mobilidade, Roaming).
 */
export function LineDetailSheet({ line, onClose }: LineDetailSheetProps) {
  const [detail, setDetail] = useState<LineDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setDetail(null);
    setError(null);

    monitorApi
      .lineDetail(line.operator.code, line.line)
      .then((response) => {
        if (current) setDetail(response.detail);
      })
      .catch((cause: Error) => {
        if (current) setError(cause.message);
      });

    return () => {
      current = false;
    };
  }, [line.operator.code, line.line]);

  const consumption = detail?.consumption ?? null;
  const usage = consumption ? formatUsage(consumption) : null;
  const hired = consumption?.hired ?? null;
  const cnpj = detail?.masterCnpj ?? detail?.identifier ?? null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 bg-[rgb(10_20_45/0.45)]"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes da linha ${formatLine(line.line)}`}
        className="animate-slide-in-right fixed inset-y-0 right-0 z-[51] flex w-[min(400px,100vw)] flex-col overflow-hidden bg-white shadow-[-24px_0_60px_rgb(6_20_54/0.25)]"
      >
        <div className="border-line-200 flex items-center gap-2.5 border-b px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-extrabold">{formatLine(line.line)}</span>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${operatorPill(line.operator.code)}`}
              >
                {line.operator.name}
              </span>
            </div>
            <div className="text-ink-600 mt-0.5 truncate text-[12px]">{lineOwner(line)}</div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-ink-400 hover:text-ink-900 p-1 text-[20px] leading-none"
          >
            ×<span className="sr-only">Fechar</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error ? (
            <div className="border-danger-200 bg-danger-100 text-danger-600 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
              {error}
            </div>
          ) : !detail ? (
            <div className="text-ink-400 py-8 text-center text-[13px]">Carregando…</div>
          ) : (
            <>
              <div className="divide-y divide-[#EEF2F8]">
                <Field label="CNPJ" value={cnpj ? formatCnpj(cnpj) : ''} />
                <Field label="Master" value={detail.master ?? ''} />
                <Field label="Cliente" value={detail.name ?? detail.company ?? ''} />
                <Field label="Conta" value={detail.account ?? ''} />
              </div>

              <div className="border-line-200 mt-4 border-t pt-4">
                <div className="text-ink-400 mb-3 text-[11px] font-extrabold uppercase tracking-wide">
                  Consumo
                </div>

                {usage ? (
                  <div className="mb-4">
                    <UsageBar
                      label="Total"
                      consumed={consumption?.consumed ?? null}
                      hired={hired}
                    />
                    <div className="text-ink-600 mt-1 text-[11.5px]" title={usageHint(consumption) ?? undefined}>
                      {usage}
                    </div>
                  </div>
                ) : (
                  <div className="text-ink-600 text-[12.5px]">Sem consumo importado.</div>
                )}

                {detail.operator.code === 'claro' ? (
                  <div className="flex flex-col gap-3.5">
                    {LINE_SEGMENTS.map((segment) => {
                      const consumed =
                        detail.segments.find((entry) => entry.key === segment.value)?.consumed ?? 0;

                      return (
                        <UsageBar
                          key={segment.value}
                          label={segment.label}
                          consumed={consumed}
                          hired={hired}
                        />
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
